// src/components/SpotlightTour.jsx
// Generic spotlight tour engine shared by ProductTour (customer dashboard
// filters) and SalesWorkspaceTour (Sales section). Each tour supplies its own
// steps + localStorage key so finishing one never marks the other as done.
//
// A step with targetId: null renders a centred welcome card (no spotlight).
// Steps whose target isn't in the DOM are skipped automatically.

import React, { useState, useEffect, useRef, useCallback } from "react";

const PAD = 8;

// Returns VIEWPORT-relative rect, no scroll offset, correct for position:fixed
function getViewportRect(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const SpotlightTour = ({
  steps: STEPS,
  storageKey: STORAGE_KEY,
  counterLabel = (step, total) => `Step ${step} of ${total}`,
  introLabel = "Quick tour",
  waitForId = null, // optional: delay auto-start until this element exists
  waitMs = 6000,
}) => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [vRect, setVRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const scrollTimerRef = useRef(null);

  // Auto-start once for new users
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    let cancelled = false;
    let timer;
    const began = Date.now();
    const tick = () => {
      if (cancelled) return;
      const ready = !waitForId || document.getElementById(waitForId);
      if (ready || Date.now() - began > waitMs) setActive(true);
      else timer = setTimeout(tick, 250);
    };
    timer = setTimeout(tick, 700);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const measureTarget = useCallback(() => {
    const current = STEPS[step];
    if (!current?.targetId) {
      setVRect(null); // welcome/no-target step
      return;
    }
    const r = getViewportRect(current.targetId);
    if (r) setVRect(r);
  }, [step]);

  // Re-measure on step change, scroll, resize
  useEffect(() => {
    if (!active) return;
    measureTarget();
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [active, step, measureTarget]);

  // Scroll target into view, then re-measure once settled
  useEffect(() => {
    if (!active) return;
    const current = STEPS[step];
    if (!current?.targetId) return;
    const el = document.getElementById(current.targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(measureTarget, 420);
    }
    return () => clearTimeout(scrollTimerRef.current);
  }, [active, step, measureTarget]);

  // Position tooltip (always fixed, viewport coords)
  useEffect(() => {
    if (!active || !tooltipRef.current) return;
    const TOOLTIP_W = 300;
    const tipH = tooltipRef.current.getBoundingClientRect().height || 170;

    if (!vRect) {
      // Welcome step, centre in viewport
      setTooltipPos({
        top: Math.max(20, window.innerHeight / 2 - tipH / 2),
        left: Math.max(12, window.innerWidth / 2 - TOOLTIP_W / 2),
      });
      return;
    }

    const hTop = vRect.top - PAD;
    const hLeft = vRect.left - PAD;
    const hW = vRect.width + PAD * 2;
    const hBottom = vRect.top + vRect.height + PAD;

    let left = hLeft + hW / 2 - TOOLTIP_W / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - TOOLTIP_W - 12));

    let top = hBottom + 12; // below by default
    if (top + tipH > window.innerHeight - 16) {
      top = hTop - tipH - 12; // flip above
    }
    if (top < 8) top = Math.max(8, window.innerHeight / 2 - tipH / 2); // last resort

    setTooltipPos({ top, left });
  }, [vRect, step, active]);

  // Skip steps whose DOM target doesn't exist (e.g. admin-only fields for non-admins)
  const nextVisibleStep = (from, direction = 1) => {
    let i = from + direction;
    while (i >= 0 && i < STEPS.length) {
      const s = STEPS[i];
      if (!s.targetId || document.getElementById(s.targetId)) return i;
      i += direction;
    }
    return direction > 0 ? STEPS.length : -1; // out of bounds = done / at start
  };

  const handleNext = () => {
    const next = nextVisibleStep(step, 1);
    if (next < STEPS.length) setStep(next);
    else handleDone();
  };

  const handlePrev = () => {
    const prev = nextVisibleStep(step, -1);
    if (prev >= 0) setStep(prev);
  };

  const handleDone = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setActive(false);
    setStep(0);
    setVRect(null);
  };

  if (!active) return null;

  const current = STEPS[step];
  const isLast = nextVisibleStep(step, 1) >= STEPS.length;
  const isFirst = step === 0;
  const hasSpotlight = !!vRect;

  const hTop = hasSpotlight ? vRect.top - PAD : 0;
  const hLeft = hasSpotlight ? vRect.left - PAD : 0;
  const hW = hasSpotlight ? vRect.width + PAD * 2 : 0;
  const hH = hasSpotlight ? vRect.height + PAD * 2 : 0;

  return (
    <>
      {/* ── Dark overlay (fixed, viewport coords) ── */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9000,
          pointerEvents: "none",
        }}
      >
        {hasSpotlight ? (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: hTop,
                background: "rgba(0,0,0,0.58)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: hTop + hH,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.58)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: hTop,
                left: 0,
                width: hLeft,
                height: hH,
                background: "rgba(0,0,0,0.58)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: hTop,
                left: hLeft + hW,
                right: 0,
                height: hH,
                background: "rgba(0,0,0,0.58)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: hTop,
                left: hLeft,
                width: hW,
                height: hH,
                border: "2px solid var(--accent-light)",
                borderRadius: 8,
                transition:
                  "top 0.28s ease, left 0.28s ease, width 0.28s ease, height 0.28s ease",
              }}
            />
          </>
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.58)",
            }}
          />
        )}
      </div>

      {/* Backdrop click = skip */}
      <div
        onClick={handleDone}
        style={{ position: "fixed", inset: 0, zIndex: 8999, cursor: "default" }}
      />

      {/* ── Tooltip (fixed, viewport coords) ── */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-live="polite"
        aria-label={`Tour step ${step + 1} of ${STEPS.length}: ${current.title}`}
        style={{
          position: "fixed",
          zIndex: 9001,
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: 300,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: "16px 18px",
          boxShadow: "var(--shadow-md)",
          fontFamily: "var(--sans)",
          transition: "top 0.28s ease, left 0.28s ease",
        }}
      >
        {/* Progress pips */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                height: 3,
                flex: 1,
                borderRadius: 9999,
                background: i <= step ? "var(--accent)" : "var(--border)",
                transition: "background 0.2s",
              }}
            />
          ))}
        </div>

        {/* Step counter */}
        <div
          style={{
            fontSize: 10,
            fontFamily: "var(--mono)",
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "var(--text-faint)",
            marginBottom: 6,
          }}
        >
          {step === 0 ? introLabel : counterLabel(step, STEPS.length - 1)}
        </div>

        {/* Title */}
        <p
          style={{
            margin: "0 0 5px",
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text)",
            letterSpacing: "-0.01em",
          }}
        >
          {current.title}
        </p>

        {/* Body */}
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 12.5,
            color: "var(--text-muted)",
            lineHeight: 1.6,
          }}
        >
          {current.body}
        </p>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={handleDone}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: 11.5,
              color: "var(--text-faint)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Skip tour
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            {!isFirst && (
              <button
                onClick={handlePrev}
                style={{
                  padding: "6px 14px",
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  fontSize: 12,
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontWeight: 500,
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: "6px 16px",
                background: "var(--accent)",
                border: "1px solid var(--accent)",
                borderRadius: 6,
                fontSize: 12,
                color: "var(--surface)",
                cursor: "pointer",
                fontFamily: "inherit",
                fontWeight: 500,
              }}
            >
              {isLast ? "Done ✓" : step === 0 ? "Show me →" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpotlightTour;
